import { useState, useEffect } from "react";
import { phoneNumberPricingService } from "@/app/_module/services/phoneNumberPricingService";

export type NoNumberReason = "trial_expired" | "subscription_expired" | "never_purchased" | null;

interface PhoneNumberValidation {
  hasActiveNumbers: boolean;
  isLoading: boolean;
  error: string | null;
  phoneNumbers: any[];
  /** Why there is no active number — null while loading or when there is one. */
  noNumberReason: NoNumberReason;
  /** The most recent disabled number, for "your number 775-… expired on Aug 5" copy. */
  expiredNumber: { phoneNumber: string; expiredAt: string | null; wasTrial: boolean } | null;
  revalidate: () => Promise<void>;
}

// Mirror the backend: only status 'active' can place a call (getUserActivePhoneNumber
// filters status = 'active'). Bruce on dev had an 'inactive' row that the old
// denylist let through, which would have hidden the banner behind a dead number.
const INACTIVE = new Set(["deleted", "disabled", "suspended", "cancelled", "canceled", "failed", "inactive", "pending", "released"]);

/**
 * Hook to check if user has active purchased phone numbers.
 * Required before making outbound calls. Mirrors the backend's own
 * classification (communication.service "no active caller id"): a disabled
 * free-trial number → trial_expired; a disabled paid number → subscription_expired;
 * nothing at all → never_purchased.
 */
export function usePhoneNumberValidation(): PhoneNumberValidation {
  const [hasActiveNumbers, setHasActiveNumbers] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [phoneNumbers, setPhoneNumbers] = useState<any[]>([]);
  const [noNumberReason, setNoNumberReason] = useState<NoNumberReason>(null);
  const [expiredNumber, setExpiredNumber] = useState<PhoneNumberValidation["expiredNumber"]>(null);

  const checkPhoneNumbers = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const numbers = await phoneNumberPricingService.getUserPhoneNumbers();

      const isGone = (num: any) => {
        const st = String(num.status || "").toLowerCase();
        return !!(num.deleted_at || num.deletedAt) || INACTIVE.has(st) || (st !== "" && st !== "active");
      };
      const activeNumbers = numbers.filter((num: any) => !isGone(num));

      setPhoneNumbers(activeNumbers);
      setHasActiveNumbers(activeNumbers.length > 0);

      if (activeNumbers.length > 0) {
        setNoNumberReason(null);
        setExpiredNumber(null);
      } else {
        const disabled = numbers.filter(isGone);
        const isTrial = (n: any) => !!(n.isFreeTrialNumber ?? n.is_free_trial_number);
        const disabledTrial = disabled.filter(isTrial);
        const disabledPaid = disabled.filter((n: any) => !isTrial(n));
        const pick = (arr: any[]) =>
          [...arr].sort((a, b) => new Date(b.updatedAt || b.updated_at || 0).getTime() - new Date(a.updatedAt || a.updated_at || 0).getTime())[0];
        if (disabledPaid.length) {
          const n = pick(disabledPaid);
          setNoNumberReason("subscription_expired");
          setExpiredNumber({ phoneNumber: n.phoneNumber || n.phone_number || "", expiredAt: n.updatedAt || n.updated_at || null, wasTrial: false });
        } else if (disabledTrial.length) {
          const n = pick(disabledTrial);
          setNoNumberReason("trial_expired");
          setExpiredNumber({
            phoneNumber: n.phoneNumber || n.phone_number || "",
            expiredAt: n.freeTrialExpiresAt || n.free_trial_expires_at || n.updatedAt || n.updated_at || null,
            wasTrial: true,
          });
        } else {
          setNoNumberReason("never_purchased");
          setExpiredNumber(null);
        }
      }
    } catch (err: any) {
      setError(err.message || "Failed to check phone numbers");
      // Don't set hasActiveNumbers to false on transient errors —
      // an API failure doesn't mean the agent has no numbers.
      // Leave the previous state so we don't block calling on network blips.
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkPhoneNumbers();
  }, []);

  return {
    hasActiveNumbers,
    isLoading,
    error,
    phoneNumbers,
    noNumberReason,
    expiredNumber,
    revalidate: checkPhoneNumbers,
  };
}
